import { createContextApi, providesOne, invalidatesOne, providesList, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';

// src/index.ts
var COMMERCE_TAGS = [
  "Product",
  "Category",
  "RateCard",
  "PricePreview",
  "Offer",
  "Coupon",
  "CouponRedemption",
  "LoyaltyProgram",
  "LoyaltyMember",
  "LoyaltyTxn",
  "CommerceDashboard",
  /** §9 B14 — a card's pictures, keyed by offer id. */
  "OfferMedia",
  /** §9 B14 — the saver's view of this tenant's live offers. */
  "StorefrontListing",
  /** T-01 — one catalogue import job and the history list. */
  "CatalogueImport"
];
var DASHBOARD_ID = "CURRENT";
var PROGRAM_ID = "CURRENT";
var commerceEndpoints = (build) => ({
  // ── Dashboard ──────────────────────────────────────────────────────────────
  getCommerceDashboard: build.query({
    query: () => ({ url: "/commerce/dashboard" }),
    providesTags: [{ type: "CommerceDashboard", id: DASHBOARD_ID }]
  }),
  // ── Products ───────────────────────────────────────────────────────────────
  /**
   * The catalogue, **accumulating**: every cursor lands in one cache entry and appends.
   *
   * Right for a window a caller wants to grow — the offer and rate-card drawers, the scope
   * directory — and wrong for a numbered pager, which is why {@link listProductsPage} exists
   * beside it rather than instead of it.
   */
  listProducts: build.query({
    query: (params) => ({ url: "/commerce/products", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Product", result)
  }),
  /**
   * The same catalogue read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listProducts} drops `cursor` from its cache key and appends every response into a
   * single entry, which is exactly right for a "load more" list and exactly wrong for a pager:
   * paging forward would grow the rendered set instead of replacing it, and paging *back* would
   * re-fetch a cursor already walked and append its rows a second time.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * Previous free — a page already visited is answered from cache rather than re-fetched.
   *
   * Both exist because both are legitimate. The pattern, and this wording, come from
   * `api-audit`'s `listEntries`/`listEntriesPage` pair.
   */
  listProductsPage: build.query({
    query: (params) => ({ url: "/commerce/products", params }),
    providesTags: (result) => providesList("Product", result)
  }),
  getProduct: build.query({
    query: (id) => ({ url: `/commerce/products/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Product", id)
  }),
  /** Always registers as `DRAFT` — activation is an explicit, separate act. */
  createProduct: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/commerce/products",
      method: "POST",
      body,
      idempotencyKey: `commerce-product-create:${draftId}`
    }),
    invalidatesTags: [
      { type: "Product", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /**
   * Also invalidates the product's own `PricePreview`: a category or attribute
   * change moves which rate card the price walk selects and what it computes,
   * so a cached breakdown for this product is stale the moment the edit lands.
   */
  updateProduct: build.mutation({
    query: ({ id, actionId, ...body }) => ({
      url: `/commerce/products/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `commerce-product-update:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Product", id),
      { type: "PricePreview", id },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /** `DRAFT`/`DISCONTINUED` → `ACTIVE`; 409 `COMMERCE_PRODUCT_ILLEGAL_TRANSITION` otherwise. */
  activateProduct: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/products/${id}/activate`,
      method: "POST",
      idempotencyKey: `commerce-product-activate:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Product", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /** `ACTIVE` → `DISCONTINUED`; 409 `COMMERCE_PRODUCT_ILLEGAL_TRANSITION` otherwise. */
  discontinueProduct: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/products/${id}/discontinue`,
      method: "POST",
      idempotencyKey: `commerce-product-discontinue:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Product", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /** The server-side rate-card walk (D3), as displayable components. */
  getProductPrice: build.query({
    query: (id) => ({ url: `/commerce/products/${id}/price` }),
    providesTags: (_r, _e, id) => [{ type: "PricePreview", id }]
  }),
  // ── Catalogue import (T-01) ────────────────────────────────────────────────
  /**
   * Import history, newest first and **capped at the 100 most recent** server-side.
   * `issues` is empty on every entry — the list never loads a job's rows.
   */
  listCatalogueImports: build.query({
    query: () => ({ url: "/commerce/imports" }),
    providesTags: (result) => providesList("CatalogueImport", result)
  }),
  getCatalogueImport: build.query({
    query: (id) => ({ url: `/commerce/imports/${id}` }),
    providesTags: (_r, _e, id) => providesOne("CatalogueImport", id)
  }),
  /**
   * Step 1 — stage the upload. Creates a `STAGED` job with no rows and registers nothing.
   * There is no multipart endpoint: the browser parses the spreadsheet and posts typed rows to
   * the dry-run, so the raw file never leaves the merchant's machine.
   */
  stageCatalogueImport: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/commerce/imports",
      method: "POST",
      body,
      idempotencyKey: `commerce-import-stage:${draftId}`
    }),
    invalidatesTags: [{ type: "CatalogueImport", id: "LIST" }]
  }),
  /**
   * Step 2 — validate up to 5,000 typed rows and get back the row number and reason of
   * everything the commit would refuse. **Writes nothing into the catalogue.**
   *
   * Re-runnable until the job is committed, which is why its key carries an `actionId`: a
   * merchant who fixes row 3,412 and posts the corrected file is making a genuinely new request,
   * and a key scoped to the job id alone would replay the first run's report.
   */
  dryRunCatalogueImport: build.mutation({
    query: ({ id, actionId, ...body }) => ({
      url: `/commerce/imports/${id}/dry-run`,
      method: "POST",
      body,
      idempotencyKey: `commerce-import-dry-run:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("CatalogueImport", id)
  }),
  /**
   * Step 3 — apply the accepted rows in one transaction. No request body: the rows the dry-run
   * persisted are the only record of what to commit.
   *
   * A one-way door, so the key is the job id alone — a retried, timed-out commit must replay the
   * first response rather than register four thousand products a second time. It invalidates the
   * product, category and rate-card lists because a commit writes into whichever one the job's
   * target names, and the dashboard because its feed and its counts both move.
   */
  commitCatalogueImport: build.mutation({
    query: ({ id }) => ({
      url: `/commerce/imports/${id}/commit`,
      method: "POST",
      idempotencyKey: `commerce-import-commit:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("CatalogueImport", id),
      { type: "Product", id: "LIST" },
      { type: "Category", id: "LIST" },
      { type: "RateCard", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  // ── Categories ─────────────────────────────────────────────────────────────
  /** The whole table — a tenant has tens of categories, not thousands. */
  listCategories: build.query({
    query: () => ({ url: "/commerce/categories" }),
    providesTags: (result) => providesList("Category", result)
  }),
  createCategory: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/commerce/categories",
      method: "POST",
      body,
      idempotencyKey: `commerce-category-create:${draftId}`
    }),
    invalidatesTags: [{ type: "Category", id: "LIST" }]
  }),
  /**
   * Also invalidates the product list: `categoryName` is denormalised onto
   * product rows, so a rename here is stale text on every row in the grid.
   */
  updateCategory: build.mutation({
    query: ({ id, actionId, ...body }) => ({
      url: `/commerce/categories/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `commerce-category-update:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Category", id),
      { type: "Product", id: "LIST" }
    ]
  }),
  /** Empty categories only — 409 `COMMERCE_CATEGORY_NOT_EMPTY` otherwise. */
  archiveCategory: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/categories/${id}/archive`,
      method: "POST",
      idempotencyKey: `commerce-category-archive:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Category", id)
  }),
  // ── Rate cards ─────────────────────────────────────────────────────────────
  /**
   * The rate book, **accumulating**: every cursor lands in one cache entry and appends.
   *
   * It used to read "the whole table — a tenant has tens of rate cards, not thousands", which
   * the service's own handler javadoc contradicted: the list grows one row per publish forever,
   * and a tenant republishing a board rate daily writes 365 cards a year. It was truncated at
   * 500 with nothing on the wire to say so. It is cursor-paged now.
   */
  listRateCards: build.query({
    query: (params) => ({ url: "/commerce/rate-cards", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("RateCard", result)
  }),
  /** The rate book, paged discretely for a numbered pager — see {@link listProductsPage}. */
  listRateCardsPage: build.query({
    query: (params) => ({ url: "/commerce/rate-cards", params }),
    providesTags: (result) => providesList("RateCard", result)
  }),
  getRateCard: build.query({
    query: (id) => ({ url: `/commerce/rate-cards/${id}` }),
    providesTags: (_r, _e, id) => providesOne("RateCard", id)
  }),
  /** Always creates a `DRAFT` — a card prices nothing until it is published. */
  createRateCard: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/commerce/rate-cards",
      method: "POST",
      body,
      idempotencyKey: `commerce-ratecard-create:${draftId}`
    }),
    invalidatesTags: [{ type: "RateCard", id: "LIST" }]
  }),
  /** Drafts only — 409 `COMMERCE_RATE_CARD_NOT_DRAFT` otherwise. */
  updateRateCard: build.mutation({
    query: ({ id, actionId, ...body }) => ({
      url: `/commerce/rate-cards/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `commerce-ratecard-update:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("RateCard", id)
  }),
  /**
   * Entity-derived key: publishing is a one-way door per card, and a retry of a
   * timed-out publish must replay the first response, never supersede twice.
   *
   * The blast radius is deliberate: publishing supersedes the previous ACTIVE
   * card for the same scope, so *every* cached price breakdown may now walk to
   * a different card (the whole `PricePreview` tag), the product grid's
   * `resolvedPrice` column moves (`Product` LIST), and the dashboard's tiles
   * are computed over the catalogue the card just re-priced.
   */
  publishRateCard: build.mutation({
    query: ({ id }) => ({
      url: `/commerce/rate-cards/${id}/publish`,
      method: "POST",
      idempotencyKey: `commerce-ratecard-publish:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("RateCard", id),
      "PricePreview",
      { type: "Product", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /** Drafts only — 409 `COMMERCE_RATE_CARD_NOT_DRAFT` otherwise. */
  discardRateCard: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/rate-cards/${id}`,
      method: "DELETE",
      idempotencyKey: `commerce-ratecard-discard:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("RateCard", id)
  }),
  // ── Offers ─────────────────────────────────────────────────────────────────
  /**
   * The offer book, **accumulating**: every cursor lands in one cache entry and appends.
   *
   * It used to read "the whole table — a tenant runs tens of offers, not thousands", and was
   * capped at 500 for symmetry with the rate-card list. Bounded in practice is not bounded, and
   * a cap nothing on the wire declares is a truncation nobody can see.
   */
  listOffers: build.query({
    query: (params) => ({ url: "/commerce/offers", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Offer", result)
  }),
  /** The offer book, paged discretely for a numbered pager — see {@link listProductsPage}. */
  listOffersPage: build.query({
    query: (params) => ({ url: "/commerce/offers", params }),
    providesTags: (result) => providesList("Offer", result)
  }),
  getOffer: build.query({
    query: (id) => ({ url: `/commerce/offers/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Offer", id)
  }),
  createOffer: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/commerce/offers",
      method: "POST",
      body,
      idempotencyKey: `commerce-offer-create:${draftId}`
    }),
    invalidatesTags: [
      { type: "Offer", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /** `DRAFT`/`SCHEDULED` only — 409 `COMMERCE_OFFER_NOT_EDITABLE` otherwise. */
  updateOffer: build.mutation({
    query: ({ id, actionId, ...body }) => ({
      url: `/commerce/offers/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `commerce-offer-update:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Offer", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /**
   * Entity-derived key: activation is a one-way door per offer — the window
   * decides whether it lands `SCHEDULED` or `ACTIVE` (D5), and a retry of a
   * timed-out activate must replay the first response, never re-run.
   */
  activateOffer: build.mutation({
    query: ({ id }) => ({
      url: `/commerce/offers/${id}/activate`,
      method: "POST",
      idempotencyKey: `commerce-offer-activate:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Offer", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  pauseOffer: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/offers/${id}/pause`,
      method: "POST",
      idempotencyKey: `commerce-offer-pause:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Offer", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  resumeOffer: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/offers/${id}/resume`,
      method: "POST",
      idempotencyKey: `commerce-offer-resume:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Offer", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  archiveOffer: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/offers/${id}/archive`,
      method: "POST",
      idempotencyKey: `commerce-offer-archive:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Offer", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  // ── The storefront card, its network flag and its pictures (§9 B14) ────────
  //
  // All six writes sit behind `commerce_offers:manage:tenant` — composing a card
  // and publishing an offer are the same act as writing the offer, and
  // TENANT_ADMIN and MARKETING both already hold that grant. No new token.
  /**
   * Replaces the whole storefront card (D10).
   *
   * Permitted on a LIVE offer, unlike `updateOffer`: INV-19 protects the
   * offer's *terms* — a customer shown "20% off until the 31st" must not find
   * them changed on the 29th — and a headline is presentation, not a term.
   */
  composeOfferStorefront: build.mutation({
    query: ({ id, actionId, ...body }) => ({
      url: `/commerce/offers/${id}/storefront`,
      method: "PUT",
      body,
      idempotencyKey: `commerce-offer-storefront:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Offer", id),
      { type: "StorefrontListing", id: "LIST" }
    ]
  }),
  /**
   * TENANT → NETWORK (D7). **Additive**: the offer keeps running at this
   * tenant's counters and keeps showing on its own storefront.
   *
   * Entity-derived key, like `activateOffer`: a retry of a timed-out push must
   * replay rather than re-run, and the service treats a second push of an
   * already-published offer as a no-op for the same reason.
   *
   * 422 `COMMERCE_OFFER_STOREFRONT_INVALID` when no card has been composed;
   * 409 unless the offer is ACTIVE or SCHEDULED.
   */
  pushOfferToNetwork: build.mutation({
    query: ({ id }) => ({
      url: `/commerce/offers/${id}/push-to-network`,
      method: "POST",
      idempotencyKey: `commerce-offer-push:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Offer", id)
  }),
  /** NETWORK → TENANT (D7) — a transition, not a delete: `pushedAt` survives. */
  withdrawOfferFromNetwork: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/offers/${id}/withdraw-from-network`,
      method: "POST",
      idempotencyKey: `commerce-offer-withdraw:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Offer", id)
  }),
  /** Every picture on the card, PENDING ones included — a failed upload shows. */
  listOfferMedia: build.query({
    query: ({ id }) => ({ url: `/commerce/offers/${id}/media` }),
    providesTags: (_r, _e, { id }) => providesOne("OfferMedia", id)
  }),
  /**
   * Mints a presigned PUT and a PENDING row.
   *
   * **No idempotency key, deliberately.** The URL is a local cryptographic
   * computation that is never persisted, so the filter has no stored response
   * to replay and answers a repeated key with a 201 and an empty body — which a
   * client cannot tell from success. Minting a second ticket is the correct
   * answer to a retry; the unconfirmed row is inert.
   *
   * The caller then PUTs the bytes to `uploadUrl` with `headers` and *nothing
   * else* — they are inside the signature — and calls `confirmOfferMediaUpload`.
   */
  requestOfferMediaUpload: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/commerce/offers/${id}/media/upload-url`,
      method: "POST",
      body,
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { id }) => providesOne("OfferMedia", id)
  }),
  /**
   * Reads the object back from storage and records what actually arrived — the
   * size and content type are storage's, never the client's claim. Returns the
   * card's whole picture list, so the console needs no refetch.
   */
  confirmOfferMediaUpload: build.mutation({
    query: ({ id, mediaId }) => ({
      url: `/commerce/offers/${id}/media/${mediaId}/confirm`,
      method: "POST",
      idempotencyKey: `commerce-offer-media-confirm:${mediaId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...providesOne("OfferMedia", id),
      ...invalidatesOne("Offer", id),
      { type: "StorefrontListing", id: "LIST" }
    ]
  }),
  /** Removes the row and the object. A genuine delete — no tombstone. */
  deleteOfferMedia: build.mutation({
    query: ({ id, mediaId }) => ({
      url: `/commerce/offers/${id}/media/${mediaId}`,
      method: "DELETE",
      idempotencyKey: `commerce-offer-media-delete:${mediaId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...providesOne("OfferMedia", id),
      ...invalidatesOne("Offer", id),
      { type: "StorefrontListing", id: "LIST" }
    ]
  }),
  // ── The saver-facing storefront (§8.4, §9 B14) ─────────────────────────────
  //
  // `commerce_storefront:read:own`, held by SAVER alone. Consumed by the PWA and
  // by `apps/portfolio` — not by the console, which has no grant for it.
  //
  // Unpaged and capped, like the console's offer grid over the same table: a
  // shop runs tens of promotions. The marketplace half (§9 B11) will be
  // cursor-paged because it spans every merchant in a federation; that is a
  // difference in the read, not in the card.
  listStorefrontListings: build.query({
    query: () => ({ url: "/commerce/storefront/listings" }),
    // The card's id key is `listingId`, not `id` — the third argument is what
    // keeps per-row invalidation working rather than silently tagging only LIST.
    providesTags: (result) => providesList("StorefrontListing", result, "listingId")
  }),
  /**
   * 404s an offer that exists but is not ACTIVE today. A link shared in a family
   * group chat outlives the promotion, and a live-looking screen for something
   * no counter will honour is worse than a not-found.
   */
  getStorefrontListing: build.query({
    query: (listingId) => ({ url: `/commerce/storefront/listings/${listingId}` }),
    providesTags: (_r, _e, listingId) => providesOne("StorefrontListing", listingId)
  }),
  // ── Coupons ────────────────────────────────────────────────────────────────
  /**
   * The coupon register, **accumulating**: every cursor lands in one cache entry and appends.
   *
   * It used to read "the whole table — a tenant runs tens of coupon codes, not thousands". That
   * was an assumption about merchant behaviour enforced nowhere: the query behind it had no
   * page, no limit and no policy cap of any kind, so a per-customer promo minting one code per
   * member serialised ten thousand rows into a single response body. It is cursor-paged now.
   */
  listCoupons: build.query({
    query: (params) => ({ url: "/commerce/coupons", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Coupon", result)
  }),
  /** The coupon register, paged discretely for a numbered pager — see {@link listProductsPage}. */
  listCouponsPage: build.query({
    query: (params) => ({ url: "/commerce/coupons", params }),
    providesTags: (result) => providesList("Coupon", result)
  }),
  getCoupon: build.query({
    query: (id) => ({ url: `/commerce/coupons/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Coupon", id)
  }),
  /** The recent counter trail, capped by the service — scoped to its coupon. */
  listCouponRedemptions: build.query({
    query: (couponId) => ({ url: `/commerce/coupons/${couponId}/redemptions` }),
    /**
     * Tagged twice: by the coupon whose trail this is, and family-wide. The
     * family tag exists for one writer — `eraseLoyaltyMember`, which clears the
     * customer's name and number from every redemption they ever made and cannot
     * know which coupons those were.
     */
    providesTags: (_r, _e, couponId) => [
      { type: "CouponRedemption", id: couponId },
      { type: "CouponRedemption", id: "LIST" }
    ]
  }),
  createCoupon: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/commerce/coupons",
      method: "POST",
      body,
      idempotencyKey: `commerce-coupon-create:${draftId}`
    }),
    invalidatesTags: [
      { type: "Coupon", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  pauseCoupon: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/coupons/${id}/pause`,
      method: "POST",
      idempotencyKey: `commerce-coupon-pause:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Coupon", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  resumeCoupon: build.mutation({
    query: ({ id, actionId }) => ({
      url: `/commerce/coupons/${id}/resume`,
      method: "POST",
      idempotencyKey: `commerce-coupon-resume:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Coupon", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /**
   * Entity-derived key: cancellation is terminal per coupon, and a retry of a
   * timed-out cancel must replay the first response, never 409 on itself.
   */
  cancelCoupon: build.mutation({
    query: ({ id }) => ({
      url: `/commerce/coupons/${id}/cancel`,
      method: "POST",
      idempotencyKey: `commerce-coupon-cancel:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Coupon", id),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  // ── Loyalty ────────────────────────────────────────────────────────────────
  getLoyaltyProgram: build.query({
    query: () => ({ url: "/commerce/loyalty/program" }),
    providesTags: [{ type: "LoyaltyProgram", id: PROGRAM_ID }]
  }),
  /**
   * Singleton — no entity id to scope the key by, so the `actionId` alone keys
   * it. Also invalidates the member list: a changed expiry horizon moves every
   * member's `pointsExpiringSoon`, and a tier-rule change moves tiers.
   */
  updateLoyaltyProgram: build.mutation({
    query: ({ actionId, ...body }) => ({
      url: "/commerce/loyalty/program",
      method: "PUT",
      body,
      idempotencyKey: `commerce-loyalty-program-update:${actionId}`
    }),
    invalidatesTags: [
      { type: "LoyaltyProgram", id: PROGRAM_ID },
      { type: "LoyaltyMember", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  listLoyaltyMembers: build.query({
    query: (params) => ({
      url: "/commerce/loyalty/members",
      params
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("LoyaltyMember", result)
  }),
  /** The member book, paged discretely for a numbered pager — see {@link listProductsPage}. */
  listLoyaltyMembersPage: build.query({
    query: (params) => ({
      url: "/commerce/loyalty/members",
      params
    }),
    providesTags: (result) => providesList("LoyaltyMember", result)
  }),
  getLoyaltyMember: build.query({
    query: (id) => ({ url: `/commerce/loyalty/members/${id}` }),
    providesTags: (_r, _e, id) => providesOne("LoyaltyMember", id)
  }),
  listLoyaltyTransactions: build.query({
    query: ({ memberId, ...params }) => ({
      url: `/commerce/loyalty/members/${memberId}/transactions`,
      params
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("LoyaltyTxn", result)
  }),
  /**
   * The points ledger, paged discretely — see {@link listProductsPage}.
   *
   * Sharper here than anywhere else in this package: the ledger is append-only, so the window
   * a "load more" list holds falls further behind the set with every transaction the tenant
   * records.
   */
  listLoyaltyTransactionsPage: build.query({
    query: ({ memberId, ...params }) => ({
      url: `/commerce/loyalty/members/${memberId}/transactions`,
      params
    }),
    providesTags: (result) => providesList("LoyaltyTxn", result)
  }),
  /**
   * Appends to the ledger, so the member's balance/tier (id + LIST), the ledger
   * itself (LIST) and the dashboard's outstanding-points tile all move.
   */
  adjustLoyaltyPoints: build.mutation({
    query: ({ id, actionId, ...body }) => ({
      url: `/commerce/loyalty/members/${id}/adjust`,
      method: "POST",
      body,
      idempotencyKey: `commerce-loyalty-adjust:${id}:${actionId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("LoyaltyMember", id),
      { type: "LoyaltyTxn", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /**
   * Entity-derived key: erasure is a one-way door per member (G-09). A retry of
   * a timed-out erase must replay the first response, and a deliberate second
   * erase is a no-op rather than a 409 — "already forgotten" is the outcome the
   * caller asked for either way, so there is no `actionId` to mint.
   *
   * Invalidates the member (its name and phone are gone), the member LIST (the
   * grid row changes) and the dashboard: the activity feed gains a row.
   *
   * **And every coupon's redemption trail**, because the erasure is not confined
   * to the member row: it sweeps `commerce_coupon_redemptions`, which carries its
   * own copy of the name and the number (a coupon redeemer is not necessarily a
   * member). Any open coupon drawer would otherwise keep showing the name the
   * service just removed. This mutation has no way to know which coupons that
   * customer used, so it invalidates the family tag `listCouponRedemptions`
   * provides alongside its per-coupon one — an erasure is rare and a trail is a
   * small read.
   *
   * The points ledger is deliberately NOT invalidated — erasure moves no points,
   * and the entries carry no name to change.
   */
  eraseLoyaltyMember: build.mutation({
    query: ({ id }) => ({
      url: `/commerce/loyalty/members/${id}/erase`,
      method: "POST",
      idempotencyKey: `commerce-loyalty-erase:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("LoyaltyMember", id),
      { type: "CouponRedemption", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  // ── The counter (apps/pos) ─────────────────────────────────────────────────
  //
  // Four writes and three reads. Every write keys its idempotency on the till's
  // own `tillTxnId`; mint it when the sale opens, not per submit.
  /**
   * Consumes one use of a promo code. A refusal is 409
   * `COMMERCE_COUPON_NOT_REDEEMABLE` with the reason as the message prefix —
   * call `previewCouponAtCounter` first if the till needs the reason typed.
   */
  redeemCouponAtCounter: build.mutation({
    query: ({ tillTxnId, ...body }) => ({
      url: "/commerce/counter/coupons/redeem",
      method: "POST",
      body: { tillTxnId, ...body },
      idempotencyKey: `commerce-counter-redeem:${tillTxnId}`
    }),
    // The response carries no couponId — the coupon was addressed by code — so the
    // list and the dashboard are what move. A console watching one coupon's
    // redemption trail refetches on the list invalidation.
    invalidatesTags: [
      { type: "Coupon", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /**
   * The dry run: the same guard as `redeemCouponAtCounter`, answered as a value
   * and writing nothing. A refusal is a 200 with `redeemable: false`.
   *
   * The only endpoint in this package that opts out of an idempotency key —
   * there is nothing to replay. It is a POST because its body carries a phone.
   */
  previewCouponAtCounter: build.mutation({
    query: (body) => ({
      url: "/commerce/counter/coupons/preview",
      method: "POST",
      body,
      skipIdempotencyKey: true
    })
  }),
  /**
   * Records an offer applied to a line of a sale. `discountAmount` is what comes
   * off this bill; `benefit` is what the offer is worth.
   */
  applyOfferAtCounter: build.mutation({
    query: ({ offerId, ...body }) => ({
      url: `/commerce/counter/offers/${offerId}/apply`,
      method: "POST",
      body,
      idempotencyKey: `commerce-counter-apply:${body.tillTxnId}:${offerId}`
    }),
    invalidatesTags: (_r, _e, { offerId }) => [
      ...invalidatesOne("Offer", offerId),
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /** Awards points, enrolling the customer on their first sale (INV-22). */
  earnPointsAtCounter: build.mutation({
    query: ({ tillTxnId, ...body }) => ({
      url: "/commerce/counter/loyalty/earn",
      method: "POST",
      body: { tillTxnId, ...body },
      idempotencyKey: `commerce-counter-earn:${tillTxnId}`
    }),
    invalidatesTags: (result) => [
      ...result ? invalidatesOne("LoyaltyMember", result.memberId) : [],
      { type: "LoyaltyMember", id: "LIST" },
      { type: "LoyaltyTxn", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /**
   * Burns points. A distinct key prefix from the earn's, so one sale may
   * legitimately do both under one `tillTxnId`.
   */
  redeemPointsAtCounter: build.mutation({
    query: ({ tillTxnId, ...body }) => ({
      url: "/commerce/counter/loyalty/redeem",
      method: "POST",
      body: { tillTxnId, ...body },
      idempotencyKey: `commerce-counter-redeem-points:${tillTxnId}`
    }),
    invalidatesTags: (result) => [
      ...result ? invalidatesOne("LoyaltyMember", result.memberId) : [],
      { type: "LoyaltyMember", id: "LIST" },
      { type: "LoyaltyTxn", id: "LIST" },
      { type: "CommerceDashboard", id: DASHBOARD_ID }
    ]
  }),
  /**
   * Resolves a member by the number the customer gave. 404 when not enrolled —
   * which is the till's cue to earn on this sale rather than to burn.
   */
  getCounterMemberByPhone: build.query({
    query: (phone) => ({
      url: "/commerce/counter/loyalty/members/by-phone",
      params: { phone }
    }),
    providesTags: (result) => result ? providesOne("LoyaltyMember", result.memberId) : []
  }),
  /**
   * The rate-card walk entered by the SKU on the tag.
   *
   * **The SKU is percent-encoded**, unlike every id in this file. `Sku`'s own
   * pattern admits `.`, `/` and `-`, so `QB/BRIDAL/001` is a legitimate SKU —
   * and interpolated raw it becomes three path segments, misses the
   * single-segment `{sku}` variable, and 404s a product the catalogue holds.
   */
  getCounterPriceBySku: build.query({
    query: (sku) => ({
      url: `/commerce/counter/products/by-sku/${encodeURIComponent(sku)}/price`
    }),
    providesTags: (result) => result ? [{ type: "PricePreview", id: result.productId }] : []
  })
});

// src/types.ts
var PRODUCT_KINDS = ["JEWELLERY", "BULLION", "FINTECH", "SERVICE"];
var PRODUCT_STATUSES = ["DRAFT", "ACTIVE", "DISCONTINUED"];
var METALS = ["GOLD", "SILVER", "PLATINUM", "DIAMOND"];
var PURITY_GRADES = ["24K", "22K", "18K", "14K", "999", "995", "925", "950"];
var FINTECH_INSTRUMENTS = [
  "MUTUAL_FUND",
  "DIGITAL_GOLD",
  "FIXED_DEPOSIT",
  "INSURANCE"
];
var RISK_LEVELS = ["LOW", "MODERATE", "HIGH"];
var CATEGORY_STATUSES = ["ACTIVE", "ARCHIVED"];
var RATE_BASES = ["METAL_LINKED", "PER_GRAM", "FIXED", "FEE_BASED"];
var RATE_CARD_STATUSES = ["DRAFT", "ACTIVE", "SUPERSEDED"];
var RATE_SCOPE_LEVELS = ["CATEGORY", "PRODUCT"];
var OFFER_TYPES = [
  "PERCENT_DISCOUNT",
  "FLAT_DISCOUNT",
  "MAKING_CHARGE_WAIVER",
  "BONUS_GRAMS",
  "CASHBACK"
];
var OFFER_SCOPES = ["ALL_PRODUCTS", "CATEGORY", "PRODUCT"];
var OFFER_AUDIENCES = ["ALL_CUSTOMERS", "SCHEME_SAVERS", "LOYALTY_MEMBERS"];
var OFFER_STATUSES = [
  "DRAFT",
  "SCHEDULED",
  "ACTIVE",
  "PAUSED",
  "EXPIRED",
  "ARCHIVED"
];
var OFFER_DISTRIBUTIONS = ["TENANT", "NETWORK"];
var OFFER_MEDIA_STATUSES = ["PENDING", "READY"];
var OFFER_VALUE_UNITS = ["PERCENT", "INR", "GRAMS"];
var LISTING_KINDS = ["OFFER", "PRODUCT"];
var LISTING_STATUSES = [
  "PENDING_REVIEW",
  "LIVE",
  "REJECTED",
  "SUSPENDED",
  "EXPIRED",
  "WITHDRAWN",
  "INELIGIBLE",
  /**
   * A newer version of the same offer has been approved in its place (D10).
   *
   * **Only the network half can produce it**, and it is declared here anyway because this union is
   * the *shared* shape, not commerce-service's own — D14 says one contract, three renderers, and a
   * renderer that had to widen a union per server would already be two contracts. The tenant half
   * has nothing to supersede: it returns a shop's own live offers, one row per offer, with no
   * versioning and no moderation.
   *
   * Added by §9 B11, which is when versioned listings first existed.
   */
  "SUPERSEDED"
];
var LISTING_AVAILABILITIES = ["ALL_BRANCHES", "BRANCHES"];
var COUPON_TYPES = ["FLAT_DISCOUNT", "PERCENT_DISCOUNT", "GIFT"];
var COUPON_STATUSES = [
  "ACTIVE",
  "SCHEDULED",
  "PAUSED",
  "EXHAUSTED",
  "EXPIRED",
  "CANCELLED"
];
var COUPON_REFUSAL_REASONS = [
  "NOT_ACTIVE",
  "NOT_STARTED",
  "EXPIRED",
  "CAP_REACHED",
  "CUSTOMER_LIMIT_REACHED",
  "MIN_PURCHASE_NOT_MET"
];
var LOYALTY_PROGRAM_STATUSES = ["ACTIVE", "PAUSED"];
var LOYALTY_TIERS = ["MEMBER", "SILVER", "GOLD", "PLATINUM"];
var LOYALTY_TXN_TYPES = ["EARN", "REDEEM", "ADJUST", "EXPIRE"];
var UNPRICED_REASONS = [
  "NO_RATE_CARD",
  "NO_WEIGHT",
  "NO_BENCHMARK",
  "FEE_BASED"
];
var OFFER_BENEFIT_KINDS = [
  "DISCOUNT",
  "CASHBACK",
  "MAKING_CHARGE_WAIVER",
  "BONUS_GRAMS"
];
var COMMERCE_ACTIVITY_KINDS = [
  "PRODUCT_REGISTERED",
  "PRODUCT_ACTIVATED",
  "RATE_CARD_PUBLISHED",
  "OFFER_ACTIVATED",
  "COUPON_CREATED",
  "COUPON_REDEEMED",
  "LOYALTY_ADJUSTED",
  "LOYALTY_MEMBER_ERASED",
  /** T-01 — a catalogue import was committed. Its summary carries the file and the counts. */
  "CATALOGUE_IMPORTED"
];
var CATALOGUE_IMPORT_TARGETS = ["PRODUCTS", "CATEGORIES", "RATE_CARDS"];
var CATALOGUE_IMPORT_STATUSES = [
  "STAGED",
  "VALIDATING",
  "VALIDATED",
  "COMMITTED",
  "FAILED"
];

// src/index.ts
var commerceApi = createContextApi({
  context: "commerce",
  reducerPath: "commerceApi",
  tagTypes: COMMERCE_TAGS,
  endpoints: commerceEndpoints
});
var COMMERCE_INVALIDATION_RULES = [
  {
    event: "pricing.updated",
    invalidate: () => ["PricePreview", { type: "Product", id: "LIST" }]
  }
];
function registerCommerceInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: commerceApi,
    rules: COMMERCE_INVALIDATION_RULES
  });
}
var {
  // Dashboard
  useGetCommerceDashboardQuery,
  // Products
  useListProductsQuery,
  useLazyListProductsQuery,
  useListProductsPageQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useActivateProductMutation,
  useDiscontinueProductMutation,
  useGetProductPriceQuery,
  // Catalogue import (T-01)
  useListCatalogueImportsQuery,
  useGetCatalogueImportQuery,
  useLazyGetCatalogueImportQuery,
  useStageCatalogueImportMutation,
  useDryRunCatalogueImportMutation,
  useCommitCatalogueImportMutation,
  // Categories
  useListCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useArchiveCategoryMutation,
  // Rate cards
  useListRateCardsQuery,
  useListRateCardsPageQuery,
  useGetRateCardQuery,
  useCreateRateCardMutation,
  useUpdateRateCardMutation,
  usePublishRateCardMutation,
  useDiscardRateCardMutation,
  // Offers
  useListOffersQuery,
  useListOffersPageQuery,
  useGetOfferQuery,
  useCreateOfferMutation,
  useUpdateOfferMutation,
  useActivateOfferMutation,
  usePauseOfferMutation,
  useResumeOfferMutation,
  useArchiveOfferMutation,
  // The storefront card, its network flag and its pictures (§9 B14)
  useComposeOfferStorefrontMutation,
  usePushOfferToNetworkMutation,
  useWithdrawOfferFromNetworkMutation,
  useListOfferMediaQuery,
  useRequestOfferMediaUploadMutation,
  useConfirmOfferMediaUploadMutation,
  useDeleteOfferMediaMutation,
  // The saver-facing storefront (§8.4, §9 B14) — SAVER only
  useListStorefrontListingsQuery,
  useGetStorefrontListingQuery,
  // Coupons
  useListCouponsQuery,
  useListCouponsPageQuery,
  useGetCouponQuery,
  useListCouponRedemptionsQuery,
  useCreateCouponMutation,
  usePauseCouponMutation,
  useResumeCouponMutation,
  useCancelCouponMutation,
  // Loyalty
  useGetLoyaltyProgramQuery,
  useUpdateLoyaltyProgramMutation,
  useListLoyaltyMembersQuery,
  useLazyListLoyaltyMembersQuery,
  useListLoyaltyMembersPageQuery,
  useGetLoyaltyMemberQuery,
  useListLoyaltyTransactionsQuery,
  useLazyListLoyaltyTransactionsQuery,
  useListLoyaltyTransactionsPageQuery,
  useAdjustLoyaltyPointsMutation,
  useEraseLoyaltyMemberMutation,
  // The counter (apps/pos). Lazy variants on the two reads: a till looks a SKU
  // or a phone up when the operator asks, not when the page mounts.
  useRedeemCouponAtCounterMutation,
  usePreviewCouponAtCounterMutation,
  useApplyOfferAtCounterMutation,
  useEarnPointsAtCounterMutation,
  useRedeemPointsAtCounterMutation,
  useGetCounterMemberByPhoneQuery,
  useLazyGetCounterMemberByPhoneQuery,
  useGetCounterPriceBySkuQuery,
  useLazyGetCounterPriceBySkuQuery
} = commerceApi;

export { CATALOGUE_IMPORT_STATUSES, CATALOGUE_IMPORT_TARGETS, CATEGORY_STATUSES, COMMERCE_ACTIVITY_KINDS, COMMERCE_INVALIDATION_RULES, COMMERCE_TAGS, COUPON_REFUSAL_REASONS, COUPON_STATUSES, COUPON_TYPES, FINTECH_INSTRUMENTS, LISTING_AVAILABILITIES, LISTING_KINDS, LISTING_STATUSES, LOYALTY_PROGRAM_STATUSES, LOYALTY_TIERS, LOYALTY_TXN_TYPES, METALS, OFFER_AUDIENCES, OFFER_BENEFIT_KINDS, OFFER_DISTRIBUTIONS, OFFER_MEDIA_STATUSES, OFFER_SCOPES, OFFER_STATUSES, OFFER_TYPES, OFFER_VALUE_UNITS, PRODUCT_KINDS, PRODUCT_STATUSES, PURITY_GRADES, RATE_BASES, RATE_CARD_STATUSES, RATE_SCOPE_LEVELS, RISK_LEVELS, UNPRICED_REASONS, commerceApi, registerCommerceInvalidation, useActivateOfferMutation, useActivateProductMutation, useAdjustLoyaltyPointsMutation, useApplyOfferAtCounterMutation, useArchiveCategoryMutation, useArchiveOfferMutation, useCancelCouponMutation, useCommitCatalogueImportMutation, useComposeOfferStorefrontMutation, useConfirmOfferMediaUploadMutation, useCreateCategoryMutation, useCreateCouponMutation, useCreateOfferMutation, useCreateProductMutation, useCreateRateCardMutation, useDeleteOfferMediaMutation, useDiscardRateCardMutation, useDiscontinueProductMutation, useDryRunCatalogueImportMutation, useEarnPointsAtCounterMutation, useEraseLoyaltyMemberMutation, useGetCatalogueImportQuery, useGetCommerceDashboardQuery, useGetCounterMemberByPhoneQuery, useGetCounterPriceBySkuQuery, useGetCouponQuery, useGetLoyaltyMemberQuery, useGetLoyaltyProgramQuery, useGetOfferQuery, useGetProductPriceQuery, useGetProductQuery, useGetRateCardQuery, useGetStorefrontListingQuery, useLazyGetCatalogueImportQuery, useLazyGetCounterMemberByPhoneQuery, useLazyGetCounterPriceBySkuQuery, useLazyListLoyaltyMembersQuery, useLazyListLoyaltyTransactionsQuery, useLazyListProductsQuery, useListCatalogueImportsQuery, useListCategoriesQuery, useListCouponRedemptionsQuery, useListCouponsPageQuery, useListCouponsQuery, useListLoyaltyMembersPageQuery, useListLoyaltyMembersQuery, useListLoyaltyTransactionsPageQuery, useListLoyaltyTransactionsQuery, useListOfferMediaQuery, useListOffersPageQuery, useListOffersQuery, useListProductsPageQuery, useListProductsQuery, useListRateCardsPageQuery, useListRateCardsQuery, useListStorefrontListingsQuery, usePauseCouponMutation, usePauseOfferMutation, usePreviewCouponAtCounterMutation, usePublishRateCardMutation, usePushOfferToNetworkMutation, useRedeemCouponAtCounterMutation, useRedeemPointsAtCounterMutation, useRequestOfferMediaUploadMutation, useResumeCouponMutation, useResumeOfferMutation, useStageCatalogueImportMutation, useUpdateCategoryMutation, useUpdateLoyaltyProgramMutation, useUpdateOfferMutation, useUpdateProductMutation, useUpdateRateCardMutation, useWithdrawOfferFromNetworkMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map